import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { useCoClient } from "../../data/store";
import {
  commitContactEdits,
  discardContactEdit,
  fetchContactBook,
  fetchContactImport,
  queueContactEdit,
  setContactIssueIgnored,
  type ContactBookDTO,
  type ContactDTO,
  type ContactImportDTO,
  type ContactIssueDTO,
  type Party,
} from "../../data/contactsApi";
import { advanceZohoConnect } from "../../data/zohoConnectApi";
import { fetchZohoRecords } from "../../data/zohoRecordsApi";
import {
  currentValue,
  fieldLabel,
  fixControl,
  groupIssues,
  listContacts,
  openIssueCount,
  openIssues,
  valueText,
  FIX_FIELD,
  type FixControl,
} from "../../domain/contacts";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { ChevronDownIcon, ChevronRightIcon, SearchIcon } from "../../components/icons";
import { StatusCard, type CardStatus } from "../../components/StatusCard";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import { useSubStepNav } from "../useSubStepNav";
import { ContactImportPanel } from "../contact-import/ContactImportPanel";

/**
 * Customers and vendors: what is in the client's contact book, what is wrong
 * with it, and two ways to fix it.
 *
 * THREE TOP-LEVEL TABS: Customers, Vendors, Upload. Customers and vendors are
 * different books with different questions — only a vendor needs a TDS
 * section, only a customer has a credit limit — and a CA works through one and
 * then the other. Inside each, Exceptions and All contacts are two ways of
 * looking at the same book. Upload holds both bulk sheets side by side, the
 * way the opening-balances step holds its invoice and bill sheets: fixing
 * forty contacts is one pasted column, and the sheet should not be buried
 * under the list it fixes.
 *
 * THE UPLOAD TAB STAYS MOUNTED while the others show. A push runs across many
 * requests, and unmounting the panel mid-push deliberately stops it — correct
 * when the screen is abandoned, wrong for a CA who pushes two hundred rows and
 * peeks at the exceptions while it runs.
 *
 * NOTHING HERE WRITES UNTIL IT IS CONFIRMED. A fix typed into a row is
 * validated on the server and queued; the bar at the top says how many are
 * waiting and sends them together. Same rhythm as the sheet below, and for the
 * same reason: clearing a contact book is one piece of work, not forty, and
 * forty unreviewed writes into a customer's books is the shape of mistake
 * nobody notices until later.
 *
 * IGNORING NEEDS A REASON. "Ignored" on its own is indistinguishable from a
 * mis-click three weeks later, and the value of this list is that what remains
 * on it is what nobody has accounted for.
 */

type View = "exceptions" | "all";
type TopTab = Party | "upload";

/**
 * How often this step nudges the contact-detail sweep along. Same beat as
 * Confirm connection's POLL_WORKING_MS, and for the same reason: we are the
 * ones doing the work, not waiting on somebody else's inbox. One nudge opens a
 * batch of forty contacts, so a book of two hundred clears in about eight.
 */
const SWEEP_POLL_MS = 1500;

/**
 * How many beats the sweep may make no progress on before we stop asking.
 *
 * A poll that changes nothing has to be able to give up. Unread contacts alone
 * no longer stall it — `advance()` re-opens a finished sweep while any contact
 * is unstamped (ZOUR-276) — but a read earlier in the queue that fails on every
 * attempt still can: contact details are read last, and a dataset ahead of them
 * that never goes green starves them.
 * Either way the CA gets a warning and a Try again rather than a spinner, and
 * Continue stays blocked, which is the honest outcome for a book we have not
 * finished reading.
 */
const SWEEP_IDLE_LIMIT = 5;

/** One sheet's contribution to its badge, same reading as opening balances. */
function importStatusOf(summary: ContactImportDTO | null): CardStatus {
  if (summary === null) return "not-started";
  if (summary.invalid_rows > 0 || summary.failed_rows > 0) return "needs-attention";

  return "uploaded";
}

export function CustomersVendorsStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "customers-vendors");

  const [book, setBook] = useState<ContactBookDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<TopTab>("customer");
  const [view, setView] = useState<View>("exceptions");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [organisationName, setOrganisationName] = useState<string | null>(null);
  const liveRef = useRef(true);

  /**
   * The contact-detail sweep stopped moving and we stopped asking. Only the
   * banner's Try again clears it — see SWEEP_IDLE_LIMIT.
   */
  const [sweepStalled, setSweepStalled] = useState(false);

  /**
   * A ref rather than the `sending` state below, because the sweep poll reads
   * it inside a running tick: it has to see the send that started after the
   * tick was scheduled, and it must not restart the poll's clock when a send
   * begins or ends.
   */
  const sendingRef = useRef(false);

  /**
   * Bumped by either upload panel whenever what is staged or pushed changes.
   * It re-reads the two summaries below, which feed the Upload tab's badge and
   * the Continue gate — those must not depend on a panel staying mounted.
   */
  const [revision, setRevision] = useState(0);
  const onImportChanged = useCallback(() => setRevision((value) => value + 1), []);

  /**
   * The two sheets' summaries, fetched by the step for the badges alone. The
   * panels hold their own richer copies; this pair only answers "does the
   * Upload tab need me".
   */
  const [imports, setImports] = useState<Record<Party, ContactImportDTO | null>>({
    customer: null,
    vendor: null,
  });

  const load = useCallback(() => {
    fetchContactBook(clientId)
      .then((result) => {
        if (!liveRef.current) return;
        setBook(result);
        setLoadError(null);
      })
      .catch((cause: unknown) => {
        if (!liveRef.current) return;
        setLoadError(cause instanceof Error ? cause.message : "Could not load the contacts.");
      });
  }, [clientId]);

  useEffect(() => {
    liveRef.current = true;
    load();

    return () => {
      liveRef.current = false;
    };
  }, [load]);

  // Read out here so the sweep effect below depends on the count alone: a book
  // replaced by a fix, a push or a re-read is not a reason to restart the
  // sweep's clock, and a changed count is the only reason there is. Zero before
  // the book has loaded, which reads correctly — nothing to sweep until we know
  // there is something to sweep.
  const pendingDetail = book?.pending_detail ?? 0;

  /**
   * Drive the contact-detail sweep from this step.
   *
   * The TDS section, MSME details and vendor type arrive one contact at a time,
   * from a read that runs behind the list — and that read only moves when
   * somebody asks it to. Confirm connection asks while it is on screen. Nothing
   * asked once the flow reached here, so a book that arrived mid-sweep stayed
   * mid-sweep for good, under a banner promising those exceptions "will appear
   * as it works through them".
   *
   * This has to exist before the Continue gate below, or blocking Continue on an
   * unfinished sweep turns a wrong answer into a dead end.
   *
   * `advance()` is documented safe to call on a poll and safe when there is
   * nothing to do. Two of these polls cannot duplicate one batch of forty
   * against the organisation's Zoho budget: `read()` opens with
   * `OnboardingZohoReadbackModel::claim()`, a conditional UPDATE to `reading`
   * whose own affected-row count is the claim, and the loser returns without
   * calling Zoho. So the worst a redundant nudge costs is a request to our own
   * backend that finds nothing to do.
   */
  useEffect(() => {
    if (pendingDetail === 0 || sweepStalled) return;

    let live = true;
    let timer: ReturnType<typeof setTimeout>;

    // Beats that moved nothing. The effect is keyed on pending_detail, so it is
    // torn down and rebuilt the moment the count changes — which means this only
    // ever counts CONSECUTIVE stalled beats, and a sweep that is working never
    // accumulates them.
    let idle = 0;

    const tick = async () => {
      // A push is already talking to Zoho for this client and its own refresh
      // writes the same book. Let it have the beat rather than racing it for the
      // rate limit and for setBook.
      if (!sendingRef.current) {
        try {
          await advanceZohoConnect(clientId);
          if (!live) return;
          // The sweep's whole product is exceptions, and they live on the book.
          load();
        } catch {
          // A failed nudge is not a failed sweep — the server keeps its own
          // state — so try again on the next beat instead of showing an error.
          if (!live) return;
        }

        idle += 1;

        if (idle >= SWEEP_IDLE_LIMIT) {
          setSweepStalled(true);

          return;
        }
      }

      timer = setTimeout(() => void tick(), SWEEP_POLL_MS);
    };

    timer = setTimeout(() => void tick(), SWEEP_POLL_MS);

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [pendingDetail, clientId, load, sweepStalled]);

  useEffect(() => {
    let live = true;

    // Zoho's own name for the organisation, for the push buttons — "which
    // books am I writing to" is the question a push button answers.
    fetchZohoRecords(clientId, ["organisation"])
      .then((records) => {
        if (live) setOrganisationName(records.organisation?.name ?? null);
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [clientId]);

  useEffect(() => {
    let live = true;

    const loadImport = (party: Party) =>
      fetchContactImport(clientId, party)
        .then((review) => {
          if (live) setImports((current) => ({ ...current, [party]: review.import }));
        })
        .catch(() => undefined);

    void loadImport("customer");
    void loadImport("vendor");

    return () => {
      live = false;
    };
  }, [clientId, revision]);

  /**
   * Every mutation returns the whole book, so the exception a fix clears
   * disappears in the same render that queues it. Nothing here re-fetches.
   */
  const apply = useCallback(async (work: Promise<ContactBookDTO>) => {
    setError(null);

    try {
      const next = await work;
      if (liveRef.current) setBook(next);
    } catch (cause: unknown) {
      if (!liveRef.current) return;
      // The server's message is written for a CA — "the check character should
      // be K, not J" — so it goes on screen as it is.
      setError(cause instanceof Error ? cause.message : "That did not work.");
    }
  }, []);

  async function sendQueued() {
    if (book === null || sending) return;

    setSending(true);
    sendingRef.current = true;
    setError(null);

    try {
      // A batch per call, so a long queue cannot outrun one request. `remaining`
      // counts only the ones still worth trying — a refused correction is left
      // out of it, which is what stops this looping against a wall.
      for (let batch = 0; batch < 200; batch++) {
        const next = await commitContactEdits(clientId);
        if (!liveRef.current) return;

        setBook(next);

        if (!next.push || next.push.remaining === 0 || next.push.halted) break;
      }
    } catch (cause: unknown) {
      if (!liveRef.current) return;
      setError(cause instanceof Error ? cause.message : "Sending the corrections stopped.");
      load();
    } finally {
      // Unconditionally, unlike the state beside it: an unmounted step has no
      // poll left to hold back, but a ref left stuck on would hold back the
      // next one.
      sendingRef.current = false;
      if (liveRef.current) setSending(false);
    }
  }

  if (!client) return null;

  const clientName = client.name || "this client";

  if (loadError !== null) {
    return (
      <SubStepShell
        title="Set Up Customers & Vendors"
        description={`Below are all the customers and vendors currently available in ${clientName}'s Zoho Books account. Zournal has synced these from Zoho Books.`}
        thenWhat="The contacts have to load before this step can be signed off."
        onContinue={() => void goNext()}
        continueDisabled
      >
        <Callout variant="warning">{loadError}</Callout>
      </SubStepShell>
    );
  }

  if (book === null) {
    return (
      <SubStepShell
        title="Set Up Customers & Vendors"
        description={`Below are all the customers and vendors currently available in ${clientName}'s Zoho Books account. Zournal has synced these from Zoho Books.`}
        thenWhat="The contacts have to load before this step can be signed off."
        onContinue={() => void goNext()}
        continueDisabled
      >
        <p className="text-sm text-muted-foreground">Reading the contacts…</p>
      </SubStepShell>
    );
  }

  const customerIssues = openIssueCount(book.contacts, "customer");
  const vendorIssues = openIssueCount(book.contacts, "vendor");
  const totalIssues = customerIssues + vendorIssues;
  const queued = book.edits.length;
  const pendingSend = book.edits.filter((edit) => edit.status === "pending").length;

  const customerSheetStatus = importStatusOf(imports.customer);
  const vendorSheetStatus = importStatusOf(imports.vendor);
  const sheetsNeedingAttention =
    (customerSheetStatus === "needs-attention" ? 1 : 0) +
    (vendorSheetStatus === "needs-attention" ? 1 : 0);

  // The contacts whose TDS section, MSME details and vendor type we have not
  // read yet. An unread contact deliberately raises no exception — saying "no
  // TDS section mapped" before looking would be a guess dressed as a finding —
  // so while this is above zero the count below is silence, not an all-clear.
  const stillReading = pendingDetail > 0;

  // The whole point of the exceptions list is that leaving it non-empty is a
  // decision someone has to own: every issue is either fixed or set aside with
  // a reason, and nothing queued is left half-sent, before the flow moves on.
  // Sheets gate the same way opening balances does: not uploading is allowed,
  // but rows that failed or arrived invalid are known defects, and moving on
  // past those is how they end up in the first month-close instead.
  //
  // THE UNFINISHED READ GATES TOO, and it is named first because it is the one
  // blocker the CA cannot see in the list itself. `totalIssues` counts the
  // exceptions we have FOUND, never whether we finished looking, so "we found
  // nothing" and "we have not looked yet" both arrive here as zero. Signing off
  // in that window means the exceptions surface only if somebody walks back to
  // this step — and a vendor left without a TDS section posts every bill
  // without TDS.
  const ready =
    !stillReading && totalIssues === 0 && queued === 0 && sheetsNeedingAttention === 0;
  const holdup = stillReading
    ? "Still reading contact details from Zoho — that has to finish before this step can be signed off."
    : totalIssues > 0
      ? "Fix or set aside every exception to continue."
      : queued > 0
        ? "Send or discard the queued corrections to continue."
        : sheetsNeedingAttention > 0
          ? "Fix the refused or invalid rows on the Upload tab to continue."
          : null;

  // Which book the two lower views read. On the Upload tab they are not
  // rendered, so the fallback never shows.
  const party: Party = tab === "vendor" ? "vendor" : "customer";

  // Whether the sheet has a second errand to offer right now: rows on screen
  // that a bulk edit would clear. It only changes the wording — the pointer
  // itself shows either way.
  const bulkFixable = view === "exceptions" && openIssueCount(book.contacts, party) > 0;

  // "clear the exceptions, or upload a corrected sheet" read as fixing errors,
  // so a CA whose upload went through cleanly had nothing here telling them the
  // sheet is also how a contact is changed afterwards.
  const description =
    `Below are all the customers and vendors currently available in ${clientName}'s Zoho Books account.` +
    ` Zournal has synced these from Zoho Books.`;

  return (
    <SubStepShell
      title="Set Up Customers & Vendors"
      description={description}
      thenWhat={holdup ?? "Continue → Set Up Opening Balances."}
      onContinue={() => void goNext()}
      continueDisabled={!ready}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 p-4">
        <p className="text-sm font-medium">
          {book.counts.customers} {book.counts.customers === 1 ? "customer" : "customers"} ·{" "}
          {book.counts.vendors} {book.counts.vendors === 1 ? "vendor" : "vendors"}
        </p>
        {/*
          "Nothing needs attention" is a verdict, and it is the line on this
          screen a CA actually reads — it wins the argument against the banner
          below it every time. So it is not allowed to be said while the read
          that finds those exceptions is still running.
        */}
        {totalIssues > 0 ? (
          <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400">
            {totalIssues} need attention
          </span>
        ) : stillReading ? (
          <span className="rounded-full border bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground">
            Still reading… {pendingDetail} left
          </span>
        ) : (
          <span className="rounded-full border bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground">
            Nothing needs attention
          </span>
        )}
      </div>

      {/*
        The TDS and MSME exceptions come from a per-contact read that runs in
        batches after the list. Until it finishes the count above is honestly
        incomplete, and saying so beats quietly under-reporting.

        The promise in the first branch is kept by the poll above, which is what
        works through them. The second branch is for when that poll has given up:
        a warning and a way back in, rather than a promise that is no longer
        true — Continue stays blocked either way, and "Save and come back later"
        is still there for anyone who would rather leave it.
      */}
      {stillReading &&
        (sweepStalled ? (
          <Callout variant="warning">
            <p>
              Reading TDS and MSME details has stopped making progress — {pendingDetail}{" "}
              {pendingDetail === 1 ? "contact is" : "contacts are"} still unread, so the
              exceptions below may be incomplete. Nothing has been lost.
            </p>
            <CoButton variant="outline" className="mt-2" onClick={() => setSweepStalled(false)}>
              Try again
            </CoButton>
          </Callout>
        ) : (
          <Callout variant="info">
            Still reading TDS and MSME details for {pendingDetail}{" "}
            {pendingDetail === 1 ? "contact" : "contacts"} - Zoho only gives those one contact
            at a time. Those exceptions will appear as it works through them.
          </Callout>
        ))}

      {error !== null && <Callout variant="warning">{error}</Callout>}

      {queued > 0 && (
        <QueuedBar
          book={book}
          sending={sending}
          pending={pendingSend}
          onSend={() => void sendQueued()}
          onDiscardAll={() => void apply(discardContactEdit(clientId))}
        />
      )}

      <TopTabs
        tab={tab}
        onChange={setTab}
        customers={book.counts.customers}
        vendors={book.counts.vendors}
        customerIssues={customerIssues}
        vendorIssues={vendorIssues}
        sheetsNeedingAttention={sheetsNeedingAttention}
      />

      {tab !== "upload" && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <ViewTabs view={view} onChange={setView} />

            <label className="relative flex min-w-52 flex-1 items-center sm:max-w-xs">
              <SearchIcon className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name or GSTIN"
                className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-sm"
              />
            </label>
          </div>

          {view === "exceptions" ? (
            <Exceptions
              book={book}
              party={party}
              search={search}
              clientId={clientId}
              onApply={apply}
            />
          ) : (
            <AllContacts book={book} party={party} search={search} />
          )}

          {/* ALWAYS, on both views and however clean the book is. The sheet is
              the editor on this step — there is no inline one by design — and
              this used to be the only thing that said so, while rendering only
              when open exceptions existed. A CA whose upload went through
              perfectly therefore saw no way to change a contact at all, and went
              and did it in Zoho instead.

              One sentence, not two: when there are exceptions to clear the
              bulk-fix errand is the same errand as an edit — same download, same
              sheet, same upload — so it joins this line rather than stacking a
              second one under it. */}
          <p className="text-sm text-muted-foreground">
            {bulkFixable
              ? "To change a contact, or fix many of these at once, download the sheet from the "
              : "To change a contact, download the sheet from the "}
            <button
              type="button"
              onClick={() => setTab("upload")}
              className="underline underline-offset-2 hover:text-foreground"
            >
              Upload tab
            </button>
            {bulkFixable ? ", edit the rows, and upload it back." : ", edit its row, and upload it back."}
          </p>
        </>
      )}

      {/* Hidden rather than unmounted: a push runs across many requests, and
          unmounting the panel mid-push deliberately stops it. Peeking at the
          exceptions while two hundred rows go through must not pause the push. */}
      <div className={cn("flex flex-col gap-4", tab !== "upload" && "hidden")}>
        <p className="text-sm text-muted-foreground">
          Want to add new customers or vendors? Go to the Upload section, download the relevant sheet, add the new
          rows, and upload the completed sheet back.
        </p>

        <StatusCard
          title="Customer sheet"
          description={`Download ${clientName}'s customers, edit them in Excel, and upload the sheet back.`}
          status={customerSheetStatus}
        >
          <ContactImportPanel
            clientId={clientId}
            party="customer"
            clientName={clientName}
            organisationName={organisationName}
            onPushed={load}
            onChanged={onImportChanged}
          />
        </StatusCard>

        <StatusCard
          title="Vendor sheet"
          description={`Download ${clientName}'s vendors, edit them in Excel, and upload the sheet back.`}
          status={vendorSheetStatus}
        >
          <ContactImportPanel
            clientId={clientId}
            party="vendor"
            clientName={clientName}
            organisationName={organisationName}
            onPushed={load}
            onChanged={onImportChanged}
          />
        </StatusCard>
      </div>
    </SubStepShell>
  );
}

// ── Chrome ────────────────────────────────────────────────────────────────

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active}
      className={cn(
        "flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

/**
 * Customers, vendors, or the upload sheets.
 *
 * A party tab carries two numbers and they mean different things: how many
 * contacts are on that side, and how many of them are asking for something.
 * The Upload tab carries only the second kind — refused or invalid sheet rows
 * would otherwise sit invisible behind it. A number only appears when it is
 * above zero — a permanent "0" beside every tab trains people to stop reading
 * the number.
 */
function TopTabs({
  tab,
  onChange,
  customers,
  vendors,
  customerIssues,
  vendorIssues,
  sheetsNeedingAttention,
}: {
  tab: TopTab;
  onChange: (tab: TopTab) => void;
  customers: number;
  vendors: number;
  customerIssues: number;
  vendorIssues: number;
  sheetsNeedingAttention: number;
}) {
  const attention = (count: number) =>
    count > 0 && (
      <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
        {count}
      </span>
    );

  return (
    <div className="flex items-center gap-1 border-b">
      {(
        [
          ["customer", "Customers", customers, customerIssues],
          ["vendor", "Vendors", vendors, vendorIssues],
        ] as const
      ).map(([value, label, count, issues]) => (
        <Tab key={value} active={tab === value} onClick={() => onChange(value)}>
          {label}
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {count}
          </span>
          {attention(issues)}
        </Tab>
      ))}
      <Tab active={tab === "upload"} onClick={() => onChange("upload")}>
        Upload
        {attention(sheetsNeedingAttention)}
      </Tab>
    </div>
  );
}

function ViewTabs({ view, onChange }: { view: View; onChange: (view: View) => void }) {
  return (
    <div className="flex items-center gap-1">
      <Tab active={view === "exceptions"} onClick={() => onChange("exceptions")}>
        Exceptions
      </Tab>
      <Tab active={view === "all"} onClick={() => onChange("all")}>
        All contacts
      </Tab>
    </div>
  );
}

/**
 * What is queued, and the one button that sends it.
 *
 * Above the list rather than below it: the corrections were made by scrolling
 * down through the exceptions, and the thing that sends them should not be
 * somewhere a CA has to scroll further to find.
 */
function QueuedBar({
  book,
  sending,
  pending,
  onSend,
  onDiscardAll,
}: {
  book: ContactBookDTO;
  sending: boolean;
  pending: number;
  onSend: () => void;
  onDiscardAll: () => void;
}) {
  const refused = book.edits.filter((edit) => edit.status === "failed");

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primary/40 bg-primary/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm">
          <span className="font-medium">
            {book.edits.length} {book.edits.length === 1 ? "correction" : "corrections"}
          </span>{" "}
          waiting. Nothing has been sent to Zoho yet.
        </p>
        <div className="flex items-center gap-2">
          <CoButton onClick={onSend} disabled={sending || pending === 0}>
            {sending ? "Sending…" : `Send ${pending} to Zoho Books`}
          </CoButton>
          <CoButton variant="outline" onClick={onDiscardAll} disabled={sending}>
            Discard all
          </CoButton>
        </div>
      </div>

      <ul className="flex flex-col gap-1">
        {book.edits.map((edit) => (
          <li key={edit.contact_id} className="flex flex-col gap-0.5 text-xs">
            <span className="flex flex-wrap items-baseline gap-1.5">
              <span className="font-medium">{edit.contact_name}</span>
              {Object.entries(edit.changes).map(([field, change]) => (
                <span key={field} className="flex items-baseline gap-1 text-muted-foreground">
                  <span>{fieldLabel(field)}</span>
                  {/* The label, not the code. "TN → MH" is what the correction
                      does and tells nobody whether it is right. */}
                  <span className="line-through opacity-60">{valueText(field, change.from, book)}</span>
                  <span aria-hidden="true">→</span>
                  <span className="font-medium text-foreground">{valueText(field, change.to, book)}</span>
                </span>
              ))}
            </span>
            {/* Zoho's own words, so the CA can see which value it objected to. */}
            {edit.error !== null && (
              <span className="text-amber-700 dark:text-amber-400">{edit.error}</span>
            )}
          </li>
        ))}
      </ul>

      {refused.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {refused.length} {refused.length === 1 ? "was" : "were"} refused and {refused.length === 1 ? "is" : "are"}{" "}
          not retried automatically - change the value to try again.
        </p>
      )}
    </div>
  );
}

// ── Exceptions ────────────────────────────────────────────────────────────

function Exceptions({
  book,
  party,
  search,
  clientId,
  onApply,
}: {
  book: ContactBookDTO;
  party: Party;
  search: string;
  clientId: ClientId;
  onApply: (work: Promise<ContactBookDTO>) => Promise<void>;
}) {
  const groups = useMemo(() => groupIssues(book, party, search), [book, party, search]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const open = openIssueCount(book.contacts, party);
  const noun = party === "vendor" ? "vendors" : "customers";

  // The second place on this screen that would otherwise hand out an all-clear
  // built from silence. The badge at the top is the one a CA reads first, but
  // this is the one sitting where the exceptions themselves would be, so it
  // gets the same treatment: an empty list is only good news once the read that
  // fills it has finished.
  const stillReading = book.pending_detail > 0;

  function toggle(kind: string) {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  if (groups.length === 0) {
    return (
      <Callout variant="info">
        {search.trim() !== ""
          ? `No exceptions match "${search.trim()}".`
          : open === 0
            ? stillReading
              ? `Nothing on the ${noun} side so far — still reading TDS and MSME details.`
              : `Nothing needs attention on the ${noun} side.`
            : `No exceptions among the ${noun}.`}
      </Callout>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {groups.map((group) => {
        const isOpen = !collapsed.has(group.kind);
        const Chevron = isOpen ? ChevronDownIcon : ChevronRightIcon;

        return (
          <li key={group.kind} className="flex flex-col gap-2 rounded-lg border p-3">
            <button
              type="button"
              onClick={() => toggle(group.kind)}
              aria-expanded={isOpen}
              className="flex items-center justify-between gap-2 text-left text-sm font-medium"
            >
              <span className="flex items-center gap-1.5">
                <Chevron className="size-4 text-muted-foreground" />
                {group.label}
              </span>
              <span className="text-xs text-muted-foreground">
                {/* Open of total, so setting one aside visibly reduces the work
                    without hiding that it was there. */}
                {group.open} of {group.rows.length}
              </span>
            </button>

            {isOpen && (
              <ul className="flex flex-col gap-2 pl-2">
                {group.rows.map((row) => (
                  <IssueRow
                    key={`${row.contact.contact_id}:${row.issue.kind}`}
                    contact={row.contact}
                    issue={row.issue}
                    book={book}
                    clientId={clientId}
                    onApply={onApply}
                  />
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function IssueRow({
  contact,
  issue,
  book,
  clientId,
  onApply,
}: {
  contact: ContactDTO;
  issue: ContactIssueDTO;
  book: ContactBookDTO;
  clientId: ClientId;
  onApply: (work: Promise<ContactBookDTO>) => Promise<void>;
}) {
  const [ignoring, setIgnoring] = useState(false);
  const control = fixControl(issue);

  return (
    <li
      className={cn(
        "flex flex-col gap-2 rounded-md border p-3 text-sm",
        issue.ignored && "bg-muted/20"
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">{contact.contact_name}</span>
        <span className="flex items-center gap-2">
          {contact.gst_no !== null && (
            <span className="font-mono text-xs text-muted-foreground">{contact.gst_no}</span>
          )}
          {/* A contact with transactions cannot be deleted in Zoho, which is
              the only answer to some of these — worth knowing before trying. */}
          {contact.has_transaction && (
            <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
              has transactions
            </span>
          )}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">{issue.message}</p>

      {issue.ignored ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Set aside - {issue.ignored_reason}</span>
          <button
            type="button"
            className="underline underline-offset-2 hover:text-foreground"
            onClick={() => void onApply(setContactIssueIgnored(clientId, issue.key, null))}
          >
            Undo
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {control === "none" ? (
            <span className="text-xs text-muted-foreground">
              Fixed in Zoho Books - merging or deleting a contact is not something this screen does.
            </span>
          ) : (
            <FixControlView
              control={control}
              contact={contact}
              issue={issue}
              book={book}
              onSave={(value) =>
                onApply(
                  queueContactEdit(clientId, contact.contact_id, {
                    [FIX_FIELD[control]]: value,
                  })
                )
              }
            />
          )}

          {ignoring ? (
            <IgnoreReason
              onCancel={() => setIgnoring(false)}
              onConfirm={(reason) => {
                setIgnoring(false);
                void onApply(setContactIssueIgnored(clientId, issue.key, reason));
              }}
            />
          ) : (
            <button
              type="button"
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              onClick={() => setIgnoring(true)}
            >
              Set aside
            </button>
          )}
        </div>
      )}
    </li>
  );
}

const INPUT_CLASS = "h-8 rounded-md border bg-background px-2 text-xs";

/**
 * The control that fixes one exception.
 *
 * Only the field the exception is about. A general contact editor belongs in
 * the spreadsheet — putting every field behind an inline control would rebuild
 * Zoho's own contact form, badly, in a list.
 *
 * A SUGGESTION IS A BUTTON, NEVER A PRE-SELECTION. The server can sometimes
 * propose a value it can defend — the state read out of the GSTIN's first two
 * digits, the PAN read out of its middle. The first version of this pre-filled
 * the picker with it, which looked helpful and was useless: the dropdown then
 * already showed the right answer, so there was nothing to change and no change
 * event to save on. Now the control holds what is actually on file, and the
 * suggestion sits beside it as one click that says what it will do.
 */
function FixControlView({
  control,
  contact,
  issue,
  book,
  onSave,
}: {
  control: Exclude<FixControl, "none">;
  contact: ContactDTO;
  issue: ContactIssueDTO;
  book: ContactBookDTO;
  onSave: (value: string) => Promise<void>;
}) {
  const current = currentValue(contact, control);
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);

  async function save(next: string) {
    setSaving(true);
    try {
      await onSave(next);
    } finally {
      setSaving(false);
    }
  }

  const suggestion =
    issue.suggestion !== undefined && issue.suggestion !== current ? issue.suggestion : null;

  const suggestionButton =
    suggestion === null ? null : (
      <CoButton variant="outline" className="h-8" disabled={saving} onClick={() => void save(suggestion)}>
        Use {suggestionLabel(control, suggestion, book)}
      </CoButton>
    );

  // The pickers save on change: there is no partial state to protect, and a
  // Save button next to a dropdown is a second click for no decision.
  if (control === "state" || control === "treatment" || control === "tds") {
    if (control === "tds" && book.tds_rates.length === 0) {
      return (
        <span className="text-xs text-muted-foreground">
          This client has no TDS rates yet - add one on the TDS &amp; TCS step first.
        </span>
      );
    }

    const options: [string, string][] =
      control === "state"
        ? Object.entries(book.states).map(([label, code]) => [code, label])
        : control === "treatment"
          ? Object.entries(book.treatments).map(([label, code]) => [code, label])
          : book.tds_rates.map((rate) => [
              rate.tax_id,
              rate.rate === null ? rate.tax_name : `${rate.tax_name} - ${rate.rate}%`,
            ]);

    const empty =
      control === "state" ? "Choose a state" : control === "treatment" ? "Choose a treatment" : "Choose a section";

    return (
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={value}
          disabled={saving}
          onChange={(event) => {
            setValue(event.target.value);
            void save(event.target.value);
          }}
          className={INPUT_CLASS}
        >
          <option value="">{empty}</option>
          {options.map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
        {suggestionButton}
      </div>
    );
  }

  const mono = control === "gstin" || control === "pan";
  const placeholder =
    control === "gstin"
      ? "27AAAAA0000A1Z5"
      : control === "pan"
        ? "AAAAA0000A"
        : control === "udyam"
          ? "UDYAM-MH-18-0000000"
          : "";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={value}
        onChange={(event) => setValue(mono ? event.target.value.toUpperCase() : event.target.value)}
        placeholder={placeholder}
        className={cn(INPUT_CLASS, mono ? "w-48 font-mono uppercase" : "w-56")}
      />
      <CoButton
        variant="outline"
        className="h-8"
        disabled={saving || value.trim() === current.trim() || value.trim() === ""}
        onClick={() => void save(value.trim())}
      >
        {saving ? "…" : "Queue fix"}
      </CoButton>
      {suggestionButton}
    </div>
  );
}

/** A suggestion names itself in the words the picker uses, not as a Zoho code. */
function suggestionLabel(control: FixControl, value: string, book: ContactBookDTO): string {
  if (control === "state") {
    const label = Object.entries(book.states).find(([, code]) => code === value)?.[0];

    return label ?? value;
  }

  if (control === "treatment") {
    const label = Object.entries(book.treatments).find(([, code]) => code === value)?.[0];

    return label ?? value;
  }

  return value;
}

/**
 * A reason is required, which is the whole point of the control.
 *
 * "Ignored" with nothing beside it is indistinguishable from a mis-click three
 * weeks later, and the value of the exceptions list is that what is left on it
 * is what nobody has accounted for.
 */
function IgnoreReason({
  onConfirm,
  onCancel,
}: {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");

  return (
    <div className="flex items-center gap-2">
      <input
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Why is this alright?"
        autoFocus
        className={cn(INPUT_CLASS, "w-56")}
      />
      <CoButton
        variant="outline"
        className="h-8"
        disabled={reason.trim() === ""}
        onClick={() => onConfirm(reason.trim())}
      >
        Set aside
      </CoButton>
      <button
        type="button"
        className="text-xs text-muted-foreground underline underline-offset-2"
        onClick={onCancel}
      >
        Cancel
      </button>
    </div>
  );
}

// ── All contacts ──────────────────────────────────────────────────────────

function AllContacts({
  book,
  party,
  search,
}: {
  book: ContactBookDTO;
  party: Party;
  search: string;
}) {
  const contacts = useMemo(() => listContacts(book, party, search), [book, party, search]);
  const noun = party === "vendor" ? "vendors" : "customers";

  if (contacts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {search.trim() !== ""
          ? `No ${noun} match "${search.trim()}".`
          : `No ${noun} were read from this client's books.`}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        {contacts.length} {contacts.length === 1 ? noun.replace(/s$/, "") : noun}
      </p>
      <ul className="flex max-h-[32rem] flex-col gap-1.5 overflow-y-auto">
        {contacts.map((contact) => (
          <ContactRow key={contact.contact_id} contact={contact} />
        ))}
      </ul>
    </div>
  );
}

function ContactRow({ contact }: { contact: ContactDTO }) {
  const open = openIssues(contact).length;

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm">
      <span className="min-w-0 flex-1 truncate font-medium">{contact.contact_name}</span>

      <span className="font-mono text-xs text-muted-foreground">{contact.gst_no ?? "No GSTIN"}</span>
      <span className="w-28 shrink-0 text-xs text-muted-foreground">
        {contact.place_of_contact_label ?? "No state"}
      </span>

      {/* A queued fix has already changed what the row shows, so the row has to
          say that what it shows is not yet what Zoho holds. */}
      {contact.queued !== null && (
        <span className="shrink-0 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
          Fix queued
        </span>
      )}

      {open > 0 && (
        <span className="shrink-0 rounded-sm bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
          {open} {open === 1 ? "issue" : "issues"}
        </span>
      )}
    </li>
  );
}
